<script setup>
import AuthLayout from "@/Layouts/AuthLayout.vue";
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import {Head, Link, useForm} from '@inertiajs/vue3';
import {inject} from "vue";
import {event} from "vue-gtag";

const route = inject('route');
const props = defineProps({
    username: {
        type: String,
        required: false,
    },
});

const form = useForm({
    username: props.username ?? '',
    password: '',
    remember: false,
});

const submit = () => {
    form.post(route('login'), {
        replace: true,
        onFinish: () => form.reset('password'),
        onSuccess: () => {
            event("login");
        },
    });
};

</script>

<template>
    <Head title="Log in"/>
    <AuthLayout title="Sign in to your account">
        <div class="mb-4 text-sm text-gray-600" v-if="form.errors.username">
            New customer?
            <Link :href="route('register', { username: form.username })"
                  class="underline hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                Create an account</Link>.
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel for="username" value="Email"/>

                <TextInput
                    id="username"
                    type="email"
                    class="mt-1 block w-full"
                    v-model="form.username"
                    required
                    :autofocus="username === null"
                    autocomplete="username"
                />

                <InputError class="mt-2" :message="form.errors.username"/>
            </div>

            <div class="mt-4">
                <InputLabel for="password" value="Password"/>

                <TextInput
                    id="password"
                    type="password"
                    class="mt-1 block w-full"
                    v-model="form.password"
                    required
                    :autofocus="username !== null"
                    autocomplete="current-password"
                />

                <InputError class="mt-2" :message="form.errors.password"/>
            </div>

            <div class="block mt-4">
                <label class="flex items-center">
                    <Checkbox name="remember" v-model:checked="form.remember"/>
                    <span class="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
            </div>

            <div class="flex items-center justify-end mt-4">
                <Link
                    :href="route('password.request')"
                    class="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Forgot your password?
                </Link>

                <PrimaryButton class="ml-4" :class="{ 'opacity-25': form.processing }"
                               :disabled="form.processing">
                    Log in
                </PrimaryButton>
            </div>
        </form>
    </AuthLayout>
</template>
